import { useLocation } from 'js/lib/useRouter';
import user from 'js/lib/user';

import type { TabName as TabNameType } from 'bundles/enterprise-legacy-learner-home/components/single-program/SingleProgramTabs';
import { TabName } from 'bundles/enterprise-legacy-learner-home/components/single-program/SingleProgramTabs';
import { isCareerAcademyFreemiumEnabled, isCareerAcademyV2Enabled } from 'bundles/unified-career-academy/utils/helpers';
import { useCareerAcademyVariant } from 'bundles/unified-common/contexts/CareerAcademyVariantContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import {
  useIsWildcard,
  useProgram,
  useProgramMembership,
} from 'bundles/unified-common/contexts/EnterpriseProgramContext';

import _t from 'i18n!nls/program-home';

export type PageNavLink = {
  label: string;
  href: string;
  isCurrent?: boolean;
};

const getLinkLabel = (link: TabNameType) => {
  switch (link) {
    case TabName.CareerAcademy:
      return _t('Find your New Career');
    case TabName.Recommendations:
      return _t('My Recommendations');
    case TabName.Catalog:
      return _t('Home');
    case TabName.SkillSets:
      return _t('SkillSets');
    case TabName.MyLearning:
      return _t('My Learning');
    default:
      return '';
  }
};

const useEnterprisePageHeaderLinks = () => {
  const isWildcard = useIsWildcard();
  const location = useLocation();
  const program = useProgram();
  const careerAcademyVariant = useCareerAcademyVariant();
  const programMembership = useProgramMembership();
  const productFeatures = useEnterpriseProductFeatures();
  const skillSetIds = program?.metadata?.linkedTargetSkillProfileIds;
  const isCareerAcademy = isCareerAcademyV2Enabled(careerAcademyVariant);
  const isCareerAcademyFreemium = isCareerAcademyFreemiumEnabled(careerAcademyVariant);
  const baseUrl = `/programs/${program?.metadata?.slug}`;

  let tabLinks: PageNavLink[] = [];

  if (!productFeatures?.isCareerAcademyLimitedCatalogProgram) {
    tabLinks.push({
      label: getLinkLabel(TabName.Catalog),
      href: baseUrl,
      isCurrent: location.pathname === baseUrl,
    });
  }

  if (
    productFeatures?.enablePersonalizedRecommendationsForProgram &&
    user.isAuthenticatedUser() &&
    programMembership?.isProgramMember &&
    isWildcard
  ) {
    const href = baseUrl + '/recommendations';

    tabLinks.push({
      label: getLinkLabel(TabName.Recommendations),
      isCurrent: location.pathname === href,
      href,
    });
  }

  if (skillSetIds?.length) {
    const href = baseUrl + '/skillsets';

    tabLinks.push({
      label: getLinkLabel(TabName.SkillSets),
      isCurrent: location.pathname === href,
      href,
    });
  }

  if (isCareerAcademy || isCareerAcademyFreemium) {
    const href = `/career-academy${baseUrl}`;

    tabLinks.push({
      label: getLinkLabel(TabName.CareerAcademy),
      isCurrent: location.pathname === href,
      href,
    });
  }

  if (
    (programMembership?.isProgramMember || programMembership?.isProgramAdmin) &&
    (user.isAuthenticatedUser() || productFeatures?.enableSkillsDashboard)
  ) {
    const href = baseUrl + '/my-learning';

    tabLinks.push({
      label: getLinkLabel(TabName.MyLearning),
      isCurrent: location.pathname === href,
      href,
    });
  }

  if (isCareerAcademyFreemium) {
    const href = `/career-academy${baseUrl}`;

    tabLinks = [
      {
        label: getLinkLabel(TabName.CareerAcademy),
        isCurrent: location.pathname === href,
        href,
      },
      ...tabLinks.filter((tabLink) => tabLink.label !== getLinkLabel(TabName.CareerAcademy)),
    ];
  }

  // If the current page is not in the tabLinks, return an empty array
  if (!tabLinks.some((link) => link.href === location.pathname)) {
    return [];
  }

  return tabLinks;
};

export default useEnterprisePageHeaderLinks;
